// Iter82 — Lightweight client-side activity tracker.
//
// `trackEvent({event_type, module, action, metadata})` POSTs to
// `/api/activity/track` as fire-and-forget. Failures are swallowed —
// tracking must never break the UI.
//
// We persist a small client-generated `session_id` in sessionStorage so
// successive events on a tab share the same session label. Backend also
// keys its own sessions by JWT jti; this client id is just an extra hint
// surfaced in `metadata.client_session`.
import axios from "axios";

const SESSION_KEY = "sitroom_client_session";

function clientSession() {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid =
        (crypto && crypto.randomUUID && crypto.randomUUID()) ||
        `cs-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return null;
  }
}

const BASE = process.env.REACT_APP_BACKEND_URL || "";
const URL = `${BASE}/api/activity/track`;

// Iter82.1 — suppress identical event_type+module fires within 300ms
// (React StrictMode double-invokes effects in dev; this halves prod payload too).
let _lastKey = "";
let _lastAt = 0;

export function trackEvent({ event_type, module, action, metadata = {} }) {
  if (!event_type) return;
  if (event_type !== "module_view" && event_type !== "action") return;
  const key = `${event_type}|${module || ""}|${action || ""}`;
  const now = Date.now();
  if (key === _lastKey && now - _lastAt < 300) return;
  _lastKey = key;
  _lastAt = now;
  const body = {
    event_type,
    module: module || null,
    action: action || null,
    metadata: { ...metadata, client_session: clientSession() },
  };
  // Fire-and-forget — withCredentials keeps the cookie session; axios
  // interceptor adds the Bearer too.
  try {
    axios.post(URL, body, { withCredentials: true }).catch(() => {});
  } catch {
    // swallow
  }
}

export function trackModuleView(modulePath, extra = {}) {
  // Map raw route → friendly module name.
  const m = String(modulePath || "").replace(/^\/+/, "").split("/")[0] || "dashboard";
  trackEvent({
    event_type: "module_view",
    module: m,
    metadata: { path: modulePath, ...extra },
  });
}

export function trackAction(action, { module: mod, ...metadata } = {}) {
  trackEvent({ event_type: "action", module: mod || null, action, metadata });
}

export default { trackEvent, trackModuleView, trackAction };
